<template>
  <k-panel
    title="Knowledge Base"
    :show-content="showContent"
    :lazy-body="true"
    @toggle="onToggle"
  >
    <template #body>
      <div class="kb-panel-body">
        <div v-if="!threadsReady" class="content-spinner" role="status" aria-label="Loading">
          <i class="fa-duotone fa-spinner fa-spin" aria-hidden="true"></i>
        </div>
        <template v-else>
          <k-table
            v-if="threads.length > 0"
            class="user-kb-table"
            :rows="tableRows"
            :headers="tableHeaders"
            :panel="false"
            :hideable="false"
            :show-controls="false"
          />
          <pagination
            v-if="numOfPages > 1"
            v-model="currentPage"
            class="user-kb-pagination"
            :count="numOfPages"
            :total-entries="threadsTotal"
            :displayed-entries="threads.length"
          />
          <p v-else-if="threads.length === 0 && threadsTotal === 0">No activity yet.</p>
        </template>
      </div>
    </template>
  </k-panel>
</template>

<style scoped>
.content-spinner {
  text-align: center;
  font-size: 1.5em;
  width: 100%;
}

.kb-panel-body {
  padding-top: 12px;
}

.user-kb-pagination {
  margin-top: 12px;
}
</style>

<script>
import KTable from '@base-components/tables/display/k-table.vue';
import KPanel from '@base-components/k-panel.vue';
import Pagination from '@base-components/k-pagination.vue';
import { ASSET_TYPES } from '@constants';

export default {
  name: 'LearnerKnowledgeBase',

  components: {
    KPanel,
    'k-table': KTable,
    pagination: Pagination,
  },

  props: {
    learnerUserId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      showContent: false,
      threads: [],
      threadsReady: false,
      threadsLoadedForLearner: false,
      threadsTotal: 0,
      currentPage: 0,
      itemsPerPage: 10,
    };
  },

  computed: {
    numOfPages() {
      if (this.threadsTotal <= this.itemsPerPage) {
        return undefined;
      }
      return Math.ceil(this.threadsTotal / this.itemsPerPage);
    },

    tableRows() {
      if (!this.threads) {
        return [];
      }
      const getPath = row => {
        if (row.module_id) {
          return {
            name: 'knowledge_thread',
            params: {
              moduleId: row.module_id,
              threadId: row.id,
            },
          };
        }
        return {
          name: 'knowledge_detached_thread',
          params: {
            threadId: row.id,
          },
        };
      };
      const output = this.threads.map(row => Object({
        id: row.id,
        question: {
          text: row.title,
          path: getPath(row),
        },
        module: row.module_name || '',
        asset: this.getAssetName(row),
        status: row.accepted_id != null ? 'Answered' : '-',
        visibility: row.is_private ? 'Private' : 'Public',
        answers: String(row.response_count ?? 0),
      }));
      return output;
    },

    tableHeaders() {
      return {
        question: {
          type: 'url',
          name: 'Question',
        },
        module: {
          name: 'Module',
        },
        asset: {
          name: 'Asset',
        },
        status: {
          name: 'Status',
          type: 'pill',
          options: {
            type: statusValue => (statusValue === 'Answered' ? 'success' : 'empty'),
          },
        },
        visibility: {
          name: 'Visibility',
          type: 'pill',
          options: {
            type: statusValue => (statusValue === 'Private' ? 'warning' : 'primary'),
          },
        },
        answers: {
          name: 'Answers',
          type: 'string',
        },
      };
    },
  },

  watch: {
    learnerUserId() {
      this.threads = [];
      this.threadsReady = false;
      this.threadsLoadedForLearner = false;
      this.threadsTotal = 0;
      this.currentPage = 0;
      if (this.showContent) {
        this.loadThreads(false);
      }
    },

    currentPage() {
      this.loadThreads(true);
    },
  },

  methods: {
    onToggle(open) {
      this.showContent = open;
      if (open && !this.threadsLoadedForLearner) {
        this.loadThreads(false);
      }
    },
    loadThreads(isPageChange = false) {
      this.$logger.info('Getting threads for learner', { learnerUserId: this.learnerUserId });
      if (isPageChange) {
        this.$Loading.minimal();
      } else {
        this.threadsReady = false;
      }
      this.$http
        .get(`/api/curriculum/knowledge/threads/user/${this.learnerUserId}`, {
          params: {
            page: this.currentPage + 1,
            limit: this.itemsPerPage,
          },
        })
        .then(response => {
          this.$logger.info('Got user questions', {
            learnerUserId: this.learnerUserId,
          });
          this.threads = response.data.threads;
          this.threadsTotal = response.data.total;
          this.threadsLoadedForLearner = true;
        })
        .catch(err => {
          if (this.$http.isWarning(err)) {
            this.$logger.warn(
              'Error getting user questions',
              { learnerUserId: this.learnerUserId },
              err,
            );
          } else {
            this.$logger.autowarn('Error getting user questions', { learnerUserId: this.learnerUserId }, err);
            this.$ktoast.showError(err);
          }
          this.threads = [];
          this.threadsTotal = 0;
        })
        .then(() => {
          this.threadsReady = true;
          if (isPageChange) {
            this.$Loading.finish();
          }
        });
    },
    getAssetName(item) {
      let assetName = '';
      Object.keys(ASSET_TYPES).forEach(key => {
        if (item[`${key}_id`]) {
          assetName = item[`${key}_name`];
        }
      });
      return assetName;
    },
  },
};
</script>
